declare var moment: moment.MomentStatic;

module ProcessEditor {
    export class ScheduleTriggerController {
        public config: IScheduleTrigger;

        public endTimeEnabled: boolean;
        public seasonEndEnabled: boolean;
        public showAdvancedSettings: boolean;

        public monthlyCycleType: string;

        public seasonStartDateCalendarOpen: boolean;
        public seasonEndDateCalendarOpen: boolean;

        public datePickerOptions: any;
        public timePickers: IScheduleTimePickers;
        public seasonDates: ISeasonDates;
        public weekdays: ICheckBoxViewModel[];
        public months: ICheckBoxViewModel[];
        public monthDays: ICheckBoxViewModel[];
        public monthRanks: ICheckBoxViewModel[];
        public monthWeekdays: ICheckBoxViewModel[];

        static $inject = ["$locale", "systemTimeZones", "currentTimeZoneName"];
        constructor(private $locale: ng.ILocaleService, public systemTimeZones: any, private currentTimeZoneName: string) {
            if (!this.config.startTime) {
                // defaults
                this.config = {
                    startTime: "00:00",
                    endTime: "00:00",
                    recurring: false,
                    repeatDelaySeconds: 0,
                    limitToOneConcurrentExecution: true,
                    cycleRecurEvery: 1,
                    cycleType: "Daily",
                    cycleLength: 1,
                    seasonStartDate: moment().format("YYYY-MM-DD"),
                    seasonEndDate: null,
                    cycleDaysOfWeek: "None",
                    cycleMonths: "None",
                    cycleDaysString: "",
                    cycleDayRanks: "None",
                    timeZone: currentTimeZoneName
                };
                this._repeatDelayType = RepeatDelayType.Minutes;

            } else {
                if (this.config.recurring) {
                    if (!this.setRepeatDelayIfFits(this.config.repeatDelaySeconds, RepeatDelayType.Hours) &&
                        !this.setRepeatDelayIfFits(this.config.repeatDelaySeconds, RepeatDelayType.Minutes) &&
                        !this.setRepeatDelayIfFits(this.config.repeatDelaySeconds, RepeatDelayType.Seconds)) {
                        throw "Could not parse repeat delay to UI: " + this.config.repeatDelaySeconds;
                    }
                }
                this.endTimeEnabled = !!this.config.endTime;
                this.seasonEndEnabled = !!this.config.seasonEndDate;

                this.monthlyCycleType = !!this.config.cycleDaysString ? MonthlyCycleType[MonthlyCycleType.OnDays] : MonthlyCycleType[MonthlyCycleType.BasedOnDayOrder];

                if (this.config.cycleType !== "Daily" || this.seasonEndEnabled || this.config.cycleLength > 1) {
                    this.showAdvancedSettings = true;
                }
            }

            this.datePickerOptions = {
                startingDay: (this.$locale.id === "fi") ? 1 : 0, // Scripts/fi/angular-locale_fi.js gets included in the bundle, and sets the locale
                showWeeks: false
            };

            this.seasonDates = {
                startDate: new Date(this.config.seasonStartDate), // Start date will always be populated
                endDate: this.config.seasonEndDate ? new Date(this.config.seasonEndDate) : null // End date can be null
            };

            this.timePickers = {
                startTime: new ScheduleTime(this.config.startTime),
                endTime: new ScheduleTime(this.config.endTime)
            };
            this.updateTimeValues();

            this.weekdays = this.getCheckBoxViewModels(this.config.cycleDaysOfWeek, ScheduleTriggerController.CYCLE_DAYS_OF_WEEK);
            this.months = this.getCheckBoxViewModels(this.config.cycleMonths, ScheduleTriggerController.CYCLE_MONTHS);
            this.monthDays = this.getCheckBoxViewModels(this.config.cycleDaysString, ScheduleTriggerController.CYCLE_MONTH_DAYS);
            this.monthRanks = this.getCheckBoxViewModels(this.config.cycleDayRanks, ScheduleTriggerController.CYCLE_MONTH_RANKS);
            this.monthWeekdays = this.getCheckBoxViewModels(this.config.cycleDaysOfWeek, ScheduleTriggerController.CYCLE_MONTHLY_DAYS_OF_WEEK);

        }

        private _repeatDelay: number;
        private _repeatDelayType: RepeatDelayType;

        public get repeatDelay() {
            return this._repeatDelay;
        }

        public get repeatDelayType(): string {
            return RepeatDelayType[this._repeatDelayType];
        }

        public set repeatDelay(value: number) {
            this._repeatDelay = value;
            this.config.repeatDelaySeconds = this.getRepeatDelayInSeconds(this._repeatDelay, this._repeatDelayType);
        }

        public set repeatDelayType(value: string) {
            this._repeatDelayType = RepeatDelayType[value];
            this.config.repeatDelaySeconds = this.getRepeatDelayInSeconds(this._repeatDelay, this._repeatDelayType);
        }

        public createArray(count) {
            let items = [];
            for (let i = 0; i < count; i++) {
                const item = i < 10 ? "0" + i : i.toString();
                items.push(item);
            }
            return items;
        }

        public updateCycleDaysOfWeek() {
            this.config.cycleDaysOfWeek = this.getStringArrayFromCheckBoxes(this.weekdays);
        }

        public updateCycleMonths() {
            this.config.cycleMonths = this.getStringArrayFromCheckBoxes(this.months);
        }

        public updateCycleMonthDaysString() {
            this.config.cycleDaysString = this.getStringArrayFromCheckBoxes(this.monthDays);

            // reset cycleDayRanks and cycleDaysOfWeek
            this.resetCycleDayRanksAndCycleDaysOfWeekForMonth();
        }

        public updateCycleDayRanks() {
            this.config.cycleDayRanks = this.getStringArrayFromCheckBoxes(this.monthRanks);

            // reset cycleDaysString
            this.resetCycleDaysString();
        }

        public updateCycleDaysOfWeekForMonth() {
            this.config.cycleDaysOfWeek = this.getStringArrayFromCheckBoxes(this.monthWeekdays);

            // reset cycleDaysString
            this.resetCycleDaysString();
        }

        public getCycleDayCheckBoxViewModels(): ICheckBoxViewModel[] {
            return this.getCheckBoxViewModels(this.config.cycleDaysOfWeek, ScheduleTriggerController.CYCLE_DAYS_OF_WEEK);
        }

        private getCheckBoxViewModels(list: string, possibleValues: ICheckBoxDataModel[]): ICheckBoxViewModel[] {
            var items = (!list || list === "None") ? [] : list.split(',');
            return possibleValues.map(v => <ICheckBoxViewModel>{
                name: v.text,
                value: v.value,
                selected: items.indexOf(v.value) !== -1
            });
        }

        private getStringArrayFromCheckBoxes(checkBoxes: ICheckBoxViewModel[]) {
            var selectedValues = _.filter(checkBoxes, item => (item.selected === true));
            return selectedValues.length > 0 ? _.map(selectedValues, item => item.value).join(",") : "None";
        }

        private static CYCLE_DAYS_OF_WEEK: ICheckBoxDataModel[] = [
            { value: "Monday", text: FRENDS.Localizations.Monday },
            { value: "Tuesday", text: FRENDS.Localizations.Tuesday },
            { value: "Wednesday", text: FRENDS.Localizations.Wednesday },
            { value: "Thursday", text: FRENDS.Localizations.Thursday },
            { value: "Friday", text: FRENDS.Localizations.Friday },
            { value: "Saturday", text: FRENDS.Localizations.Saturday },
            { value: "Sunday", text: FRENDS.Localizations.Sunday },
        ]

        private static CYCLE_MONTHLY_DAYS_OF_WEEK: ICheckBoxDataModel[] =
        ScheduleTriggerController.CYCLE_DAYS_OF_WEEK.concat([
            { value: "weekDay", text: FRENDS.Localizations.weekday },
            { value: "weekendDay", text: FRENDS.Localizations.weekendDay }
        ]);

        private static CYCLE_MONTHS: ICheckBoxDataModel[] = [
            { value: "January", text: FRENDS.Localizations.January },
            { value: "February", text: FRENDS.Localizations.February },
            { value: "March", text: FRENDS.Localizations.March },
            { value: "April", text: FRENDS.Localizations.April },
            { value: "May", text: FRENDS.Localizations.May },
            { value: "June", text: FRENDS.Localizations.June },
            { value: "July", text: FRENDS.Localizations.July },
            { value: "August", text: FRENDS.Localizations.August },
            { value: "September", text: FRENDS.Localizations.September },
            { value: "October", text: FRENDS.Localizations.October },
            { value: "November", text: FRENDS.Localizations.November },
            { value: "December", text: FRENDS.Localizations.December }
        ];

        private static CYCLE_MONTH_DAYS: ICheckBoxDataModel[] =
        _.range(1, 31, 1)
            .map(i => <ICheckBoxDataModel>{ value: i.toString(), text: i.toString() })
            .concat({ value: "Last", text: FRENDS.Localizations.Last });

        private static CYCLE_MONTH_RANKS: ICheckBoxDataModel[] = [
            { value: "First", text: FRENDS.Localizations.First },
            { value: "Second", text: FRENDS.Localizations.Second },
            { value: "Third", text: FRENDS.Localizations.Third },
            { value: "Fourth", text: FRENDS.Localizations.Fourth },
            { value: "Last", text: FRENDS.Localizations.Last }
        ];

        public toggleEndTimeEnabled(triggersForm: ng.IFormController) {
            if (this.endTimeEnabled) {
                this.endTimeEnabled = false;
                this.timePickers.endTime.hour = "00";
                this.timePickers.endTime.minute = "00";
                this.updateTimeValues();
            } else {
                this.endTimeEnabled = true;
            }
            triggersForm.$setDirty();
        }


        public toggleSeasonEndDateEnabled(triggersForm: ng.IFormController) {
            if (this.seasonEndEnabled) {
                this.seasonEndEnabled = false;
                this.seasonDates.endDate = null;
                this.formatDatePickerValue("seasonEndDate", this.seasonDates.endDate);
            } else {
                this.seasonEndEnabled = true;
            }
            triggersForm.$setDirty();
        }

        public updateTimeValues() {
            this.config.startTime = `${this.timePickers.startTime.hour}:${this.timePickers.startTime.minute}`;
            this.config.endTime = `${this.timePickers.endTime.hour}:${this.timePickers.endTime.minute}`;
        }

        public formatDatePickerValue(key, value) {
            this.config[key] = value ? moment(value).format("YYYY-MM-DD") : null;
        }

        private resetCycleDaysString() {
            _.each(this.monthDays, (monthDay) => {
                monthDay.selected = false;
            });

            this.config.cycleDaysString = "";
        }

        private resetCycleDayRanksAndCycleDaysOfWeekForMonth() {
            _.each(this.monthRanks, (monthRank) => {
                monthRank.selected = false;
            });

            this.config.cycleDayRanks = "None";

            _.each(this.monthWeekdays, (monthWeekday) => {
                monthWeekday.selected = false;
            });

            this.config.cycleDaysOfWeek = "None";
        }

        private getRepeatDelayInSeconds(delay: number, repeatDelayType: RepeatDelayType): number {
            return delay * repeatDelayType;
        }

        private setRepeatDelayIfFits(delayInSeconds: number, repeatDelayType: RepeatDelayType): boolean {
            if (delayInSeconds % repeatDelayType !== 0) {
                return false;
            }

            this._repeatDelay = delayInSeconds / repeatDelayType;
            this._repeatDelayType = repeatDelayType;
            return true;
        }
    }

    app.controller("ScheduleTriggerController", ScheduleTriggerController);

    export function scheduleTriggerDirective(): ng.IDirective {
        return {
            restrict: "EA",
            templateUrl: "scheduleTriggerEditor.htm",
            controller: "ScheduleTriggerController",
            controllerAs: "ctrl",
            bindToController: true,
            scope: {
                config: "="
            }
        }
    }

    app.directive("frendsScheduleTrigger", [scheduleTriggerDirective]);

    export interface IScheduleTriggerScope {
        config?: IScheduleTrigger;
    }

    interface ICheckBoxDataModel {
        value: string;
        text: string;
    }

    export enum RepeatDelayType {
        Seconds = 1,
        Minutes = 60,
        Hours = 60 * 60
    }

    export interface IScheduleTrigger {
        startTime: string;
        endTime: string;
        recurring: boolean;
        repeatDelaySeconds: number;
        limitToOneConcurrentExecution: boolean;
        cycleType: string;
        cycleRecurEvery: number;
        cycleLength: number;
        seasonStartDate: string;
        seasonEndDate: string;
        cycleDaysOfWeek: string;
        cycleMonths: string;
        cycleDaysString: string;
        cycleDayRanks: string;
        timeZone: string;
    }

    export interface ISeasonDates {
        startDate: Date;
        endDate: Date;
    }

    export interface IScheduleTimePickers {
        startTime: ScheduleTime;
        endTime: ScheduleTime;
    }

    interface ICheckBoxViewModel {
        name: string;
        value: string;
        selected: boolean;
    }

    export class ScheduleTime {
        hour: string;
        minute: string;
        constructor(private timeString: string) {
            // Defaults to '00' if the format doesn't match, or if the value is too high.
            this.hour = "00";
            this.minute = "00";

            // The timeString is in the format "HH:mm"
            const formatMatch = timeString.match(/^\d{2}:\d{2}/) || [];

            if (formatMatch.length === 1) {
                const parsedHour = timeString.split(":")[0];
                const parsedMinute = timeString.split(":")[1];
                this.hour = (parseInt(parsedHour) < 24) ? parsedHour : this.hour;
                this.minute = (parseInt(parsedMinute) < 60) ? parsedMinute : this.minute;
            }
        }
    }

    enum MonthlyCycleType {
        OnDays,
        BasedOnDayOrder
    }
}